import PropTypes from "prop-types";
import { useCallback, useMemo, useRef, useState } from "react";
import { FiCheck, FiCopy, FiMail, FiShare2, FiSmartphone } from "react-icons/fi";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";
import Button from "components/Button";
import ActionMenuPopover from "components/common/ActionMenuPopover";
import swtAlert from "lib/sweetAlert";

const toText = (v) => String(v ?? "").trim();

function canUseNativeShare(payload) {
  if (typeof window === "undefined") return false;
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.share !== "function") return false;
  if (typeof navigator.canShare === "function") return navigator.canShare(payload);
  return true;
}

function buildSocialUrl(platform, { url, title, text }) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || "");
  const encodedText = encodeURIComponent(text || "");

  switch (platform) {
    case "email": {
      const subject = encodedTitle;
      const body = encodeURIComponent([text, url].filter(Boolean).join("\n\n"));
      return `mailto:?subject=${subject}&body=${body}`;
    }
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case "linkedin":
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText || encodedTitle}`;
    case "whatsapp":
      return `https://wa.me/?text=${encodeURIComponent([text, url].filter(Boolean).join(" "))}`;
    default:
      return url;
  }
}

/**
 * @param {object} props
 * @param {string} [props.url] Defaults to the current page URL.
 * @param {string} [props.title] Share title / email subject.
 * @param {string} [props.text] Optional description for share body.
 * @param {string} [props.ariaLabel]
 * @param {'start' | 'end'} [props.align]
 * @param {object} [props.buttonProps] Passed to `components/Button`.
 * @param {string} [props.className] Classes for the trigger button.
 */
export function AppShareButton({
  url,
  title = "",
  text = "",
  ariaLabel = "Share",
  align = "end",
  className,
  buttonProps,
}) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const sharePayload = useMemo(() => {
    const resolvedUrl =
      url ||
      (typeof window !== "undefined" ? window.location.href : "");
    return { url: resolvedUrl, title, text };
  }, [url, title, text]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(sharePayload.url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      await swtAlert.error("Copy failed", "Could not copy the link. Please copy it manually.");
    }
  }, [sharePayload.url]);

  const handleNativeShare = useCallback(async () => {
    try {
      await navigator.share(sharePayload);
    } catch (err) {
      if (err?.name !== "AbortError") {
        await swtAlert.error("Share failed", err?.message || "Could not share this link.");
      }
    }
  }, [sharePayload]);

  const handleOpenLink = useCallback((href) => {
    const link = toText(href);
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
  }, []);

  const triggerClassName = [
    "!inline-flex !items-center !justify-center !rounded-full !h-8 !w-8 !px-0 !py-0",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        ref={anchorRef}
        className="inline-flex"
      >
        <Button
          type="button"
          variant="secondary"
          className={triggerClassName}
          aria-label={ariaLabel}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          {...buttonProps}
        >
          <FiShare2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <ActionMenuPopover
        anchorRef={anchorRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        align={align}
        className="min-w-[12rem] w-52"
      >
        <div className="p-1">
          <button
            type="button"
            onClick={() => {
              handleCopyLink();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            {linkCopied ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
            {linkCopied ? "Copied!" : "Copy link"}
          </button>

          {canUseNativeShare(sharePayload) ? (
            <button
              type="button"
              onClick={async () => {
                setOpen(false);
                await handleNativeShare();
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
            >
              <FiSmartphone className="h-4 w-4" />
              Share via device
            </button>
          ) : null}

          <div className="my-1 h-px bg-slate-200" />

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handleOpenLink(buildSocialUrl("email", sharePayload));
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            <FiMail className="h-4 w-4" />
            Email
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handleOpenLink(buildSocialUrl("facebook", sharePayload));
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            <FaFacebookF className="h-4 w-4" aria-hidden />
            Facebook
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handleOpenLink(buildSocialUrl("twitter", sharePayload));
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            <FaTwitter className="h-4 w-4" aria-hidden />
            X (Twitter)
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handleOpenLink(buildSocialUrl("linkedin", sharePayload));
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            <FaLinkedinIn className="h-4 w-4" aria-hidden />
            LinkedIn
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              handleOpenLink(buildSocialUrl("whatsapp", sharePayload));
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-700 transition hover:bg-slate-50"
          >
            <FaWhatsapp className="h-4 w-4" aria-hidden />
            WhatsApp
          </button>
        </div>
      </ActionMenuPopover>
    </>
  );
}

AppShareButton.propTypes = {
  url: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  ariaLabel: PropTypes.string,
  align: PropTypes.oneOf(["start", "end"]),
  buttonProps: PropTypes.object,
  className: PropTypes.string,
};
